import type { Editor } from '@tiptap/react';
import type { DocumentBlock } from '@core/types/document';
import { UpdateBlock } from '@core/api/endpoints/document/update_block';
import { plainTextToRichText } from '@/components/document/editor/richtext-convert';
import { loadAllDocumentBlocks } from './loadAllBlocks';

export const DOCUMENT_BLOCK_REVIEW_KIND = 'document_block';
export const DOCUMENT_SUGGESTION_REVIEW_KIND = 'document_suggestion';
export const REVIEW_ANCHOR_TEXT_LIMIT = 240;

export type DocumentReviewKind = typeof DOCUMENT_BLOCK_REVIEW_KIND | typeof DOCUMENT_SUGGESTION_REVIEW_KIND;
export type DocumentReviewStatus = 'open' | 'resolved' | 'accepted' | 'rejected';

export interface DocumentReviewAnchor {
  blockId: string;
  anchorText?: string;
  documentVersion?: string;
}

interface DocumentReviewMetadataInput {
  documentId: string;
  documentVersion: string | number;
  blockId: string;
  anchorText?: string;
  reviewKind?: DocumentReviewKind;
  suggestedText?: string;
}

export function normalizeReviewAnchorText(text: string): string {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= REVIEW_ANCHOR_TEXT_LIMIT) return normalized;
  return `${normalized.slice(0, REVIEW_ANCHOR_TEXT_LIMIT - 1).trimEnd()}…`;
}

function expectedDocumentVersion(value: unknown): number | null {
  const normalized = String(value ?? '').trim();
  if (!/^\d+$/.test(normalized)) return null;
  const version = Number(normalized);
  return Number.isSafeInteger(version) ? version : null;
}

export function buildDocumentReviewMetadata({
  documentId,
  documentVersion,
  blockId,
  anchorText = '',
  reviewKind = DOCUMENT_BLOCK_REVIEW_KIND,
  suggestedText = '',
}: DocumentReviewMetadataInput): Record<string, string> | null {
  const normalizedDocumentId = documentId.trim();
  const normalizedVersion = String(documentVersion).trim();
  const normalizedBlockId = blockId.trim();
  if (!normalizedDocumentId || !normalizedVersion || normalizedVersion === 'NaN' || !normalizedBlockId) return null;
  const isSuggestion = reviewKind === DOCUMENT_SUGGESTION_REVIEW_KIND;
  if (isSuggestion && (expectedDocumentVersion(normalizedVersion) === null || !suggestedText.trim())) return null;

  return {
    review_kind: reviewKind,
    document_id: normalizedDocumentId,
    document_version: normalizedVersion,
    block_id: normalizedBlockId,
    anchor_text: normalizeReviewAnchorText(anchorText),
    ...(isSuggestion ? { suggested_text: suggestedText } : {}),
    review_status: 'open',
  };
}

export function documentReviewStatus(message: any): DocumentReviewStatus {
  const status = message?.metadata?.review_status;
  return status === 'resolved' || status === 'accepted' || status === 'rejected' ? status : 'open';
}

export function buildDocumentReviewStatusRequest(message: any): {
  content: string;
  metadata: Record<string, string>;
} {
  return {
    content: typeof message?.content === 'string' ? message.content : '',
    metadata: {
      review_status: documentReviewStatus(message) === 'open' ? 'resolved' : 'open',
    },
  };
}

export function buildDocumentSuggestionStatusRequest(
  message: any,
  reviewStatus: 'accepted' | 'rejected',
): { content: string; metadata: Record<string, string> } {
  return {
    content: typeof message?.content === 'string' ? message.content : '',
    metadata: { review_status: reviewStatus },
  };
}

export function isDocumentSuggestion(message: any, documentId?: string): boolean {
  const metadata = message?.metadata;
  return metadata?.review_kind === DOCUMENT_SUGGESTION_REVIEW_KIND
    && (!documentId || metadata?.document_id === documentId);
}

export function reviewAnchorFromMessage(message: any, documentId: string): DocumentReviewAnchor | null {
  const metadata = message?.metadata;
  const isReview = metadata?.review_kind === DOCUMENT_BLOCK_REVIEW_KIND
    || metadata?.review_kind === DOCUMENT_SUGGESTION_REVIEW_KIND;
  if (!isReview || metadata?.document_id !== documentId) return null;
  const blockId = typeof metadata?.block_id === 'string' ? metadata.block_id.trim() : '';
  if (!blockId) return null;
  return {
    blockId,
    anchorText: typeof metadata?.anchor_text === 'string' ? metadata.anchor_text : '',
    documentVersion: typeof metadata?.document_version === 'string' ? metadata.document_version : undefined,
  };
}

function findBlock(blocks: DocumentBlock[], blockId: string): DocumentBlock | null {
  for (const block of blocks) {
    if (block.entity?.id === blockId) return block.is_deleted ? null : block;
    const child = findBlock(block.children ?? [], blockId);
    if (child) return child;
  }
  return null;
}

export function buildDocumentSuggestionUpdateRequest(
  block: DocumentBlock,
  suggestedText: string,
  baseVersion: string | number,
): { block: DocumentBlock; expected_version: number } {
  const expectedVersion = expectedDocumentVersion(baseVersion);
  if (expectedVersion === null) throw new Error('Document suggestion has no valid base version');
  return {
    block: { ...block, rich_text: plainTextToRichText(suggestedText) },
    expected_version: expectedVersion,
  };
}

export async function acceptDocumentSuggestion(
  message: any,
  {
    loadBlocks = loadAllDocumentBlocks,
    updateBlock = UpdateBlock,
    onAccepted,
  }: {
    loadBlocks?: (documentId: string) => Promise<DocumentBlock[]>;
    updateBlock?: (documentId: string, blockId: string, request: any) => Promise<unknown>;
    onAccepted?: () => void;
  } = {},
): Promise<void> {
  const metadata = message?.metadata;
  if (!isDocumentSuggestion(message) || documentReviewStatus(message) !== 'open') {
    throw new Error('Document suggestion is not open');
  }
  const documentId = typeof metadata?.document_id === 'string' ? metadata.document_id : '';
  const blockId = typeof metadata?.block_id === 'string' ? metadata.block_id : '';
  const suggestedText = typeof metadata?.suggested_text === 'string' ? metadata.suggested_text : '';
  if (!documentId || !blockId || !suggestedText.trim()) throw new Error('Document suggestion is incomplete');

  const blocks = await loadBlocks(documentId);
  const block = findBlock(blocks, blockId);
  if (!block) throw new Error('Document suggestion block is missing');
  await updateBlock(
    documentId,
    blockId,
    buildDocumentSuggestionUpdateRequest(block, suggestedText, metadata.document_version),
  );
  onAccepted?.();
}

function blockIdAtPosition(position: any): string {
  for (let depth = position?.depth ?? 0; depth > 0; depth -= 1) {
    const blockId = position.node(depth)?.attrs?.blockId;
    if (typeof blockId === 'string' && blockId) return blockId;
  }
  return '';
}

export function reviewAnchorFromEditor(editor: Editor, hoveredBlockId?: string): DocumentReviewAnchor | null {
  const selection = editor.state.selection;
  const fromBlockId = blockIdAtPosition((selection as any).$from);
  const blockId = hoveredBlockId || fromBlockId;
  if (!blockId) return null;

  const toBlockId = blockIdAtPosition((selection as any).$to);
  const anchorText = !selection.empty && fromBlockId === blockId && toBlockId === blockId
    ? normalizeReviewAnchorText(editor.state.doc.textBetween(selection.from, selection.to, ' '))
    : '';
  return { blockId, anchorText };
}

export function scrollToDocumentReviewAnchor(anchor: DocumentReviewAnchor, root: ParentNode = document): boolean {
  const elements = Array.from(root.querySelectorAll<HTMLElement>('[data-block-id]'));
  const target = elements.find((element) => element.getAttribute('data-block-id') === anchor.blockId);
  if (!target) return false;
  target.scrollIntoView({ block: 'center', behavior: 'smooth' });
  return true;
}
